import _ from "lodash";
import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Loader, Message } from "semantic-ui-react";
import queryString from "query-string";
import { useForm, FormProvider } from "react-hook-form";
import { useParams, Redirect } from "react-router-dom";
import { logout, login } from "../../actions/identity";
import { getClient } from "../../actions/clients";
import identitySelectors from "../../selectors/identity";
import clientsSelectors from "../../selectors/clients";
import LoginForm from "../../components/auth/LoginForm";
import "./Login.scss";
import { AuthOrigin, useOAuth } from "../../hooks/useOAuth";
import { LoginFailureMessage } from "../../components/auth/LoginFailureMessage";
import EmployeeAuthFormWrapper from "../../components/auth/EmployeeAuthFormWrapper";
import AppSettings from "../../config.js";

export default function Login() {
  const query = queryString.parse(window.location.search);
  const loginForm = useForm({
    defaultValues: {
      email: query.email || "",
      password: "",
      remember: true,
    },
  });
  const dispatch = useDispatch();
  const params = useParams<{ site: string; invalid?: string }>();
  const remember = loginForm.watch("remember");
  const email = loginForm.watch("email");
  const submit = ({ email, password }: any) => {
    dispatch(login({ email, password, site: params.site, employee: true }));
  };
  const { initiateOAuth, error } = useOAuth({ isEmployee: true, authOrigin: AuthOrigin.Login });

  const isLoggedIn = identitySelectors.isLoggedIn();
  const postForgotPassword = useSelector(identitySelectors.postForgotPassword);
  const postLogin = useSelector(identitySelectors.postLogin);
  const getClientSelector = useSelector(clientsSelectors.getClient);

  const sentForgotPassword = !postForgotPassword.loading && postForgotPassword.payload;
  const loginFailure = !sentForgotPassword && (postLogin.error || error || query.authErr);
  const requireVerification = loginFailure && postLogin.error?.status === 403;
  const authenticationType = _.get(getClientSelector, "payload.data.attributes.authentication-type");
  const isLDAP = authenticationType === "ldap";
  const isCitizen = identitySelectors.isCitizen();
  const isLoginLoading = _.get(postLogin, "loading", false);

  if (params.site !== params.site.toLowerCase()) {
    window.location.href = `/${params.site.toLowerCase()}/login`;
  }

  const [adminMode, setAdminMode] = useState(false);
  useEffect(() => {
    if (query.adminMode) setAdminMode(query.adminMode == "true");
  }, [query.adminMode]);
  useEffect(() => {
    let lastPressTime = 0;
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === "A") {
        e.preventDefault();

        const currentTime = new Date().getTime();

        if (currentTime - lastPressTime < 1000) {
          setAdminMode((prev) => !prev);
          lastPressTime = 0;
        } else {
          lastPressTime = currentTime;
        }
      }
    };

    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  const message = useMemo(() => {
    if (loginFailure) return <LoginFailureMessage />;
    if (query.message) {
      // Remove the message from the URL after displaying it
      const newQuery = { ...query };
      delete newQuery.message;
      window.history.replaceState({}, "", `/${params.site}/login?${queryString.stringify(newQuery)}`);
      return (
        <Message className='feedback' positive>
          {query.message}
        </Message>
      );
    }
    return undefined;
  }, [loginFailure, query.message]);

  const renderSentForgotPassword = () => (
    <Message className='feedback' positive>
      Please check your email for login instructions
    </Message>
  );

  useEffect(() => {
    if (!getClientSelector.payload) dispatch(getClient(params.site));
  }, [dispatch, getClientSelector.payload, params.site]);
  useEffect(() => {
    if (query.invalid) {
      //error case where the query string is invalid
      const citizen = isCitizen ?? false;
      dispatch(logout(params.site, citizen));
    }
  }, [dispatch, params.site, query.invalid]);

  if (requireVerification) {
    const target =
      (isCitizen ?? false) &&
      !query.redirect?.includes("public") &&
      !query.redirect?.includes(AppSettings.GOGOV_WEB_URL)
        ? null
        : query.redirect || null;
    return (
      <Redirect
        to={{
          pathname: `/${params.site}/public/verify`,
          search: queryString.stringify({
            email,
            ...(target ? { redirect: target } : {}),
          }),
        }}
      />
    );
  }

  if (isLoggedIn && !query.invalid) {
    const target =
      (isCitizen ?? false) &&
      !query.redirect?.includes("public") &&
      !query.redirect?.includes(AppSettings.GOGOV_WEB_URL)
        ? null
        : query.redirect || null;
    return (
      <Redirect
        to={{
          pathname: `/${params.site}/landing`,
          search: queryString.stringify({
            remember,
            ...(target ? { redirect: target } : {}),
          }),
        }}
      />
    );
  }

  if (!authenticationType) {
    return <Loader active />;
  }

  return (
    <EmployeeAuthFormWrapper
      title={adminMode ? "Sign in as User" : "Sign in"}
      documentTitle='Login'
      adminMode={adminMode}
      onLogoClick={() => setAdminMode((prev) => !prev)}
    >
      {sentForgotPassword ? renderSentForgotPassword() : null}
      <FormProvider {...loginForm}>
        <LoginForm
          onSubmit={submit}
          message={message}
          site={params.site}
          username={{
            label: isLDAP ? "Username" : "Email",
            placeholder: isLDAP ? "Username" : "Email",
          }}
          isLoading={isLoginLoading}
          initiateOAuth={initiateOAuth}
          adminMode={adminMode}
        />
      </FormProvider>
    </EmployeeAuthFormWrapper>
  );
}
